import { useState, FormEvent } from "react";
import { Box, Typography, Grid, TextField, Button, Checkbox, FormControlLabel } from "@mui/material";
import { NumberField } from "../../shared/components/NumberField";
import { SharedMUIList } from "../../shared/components/SharedMUIList";
import { getStates, getStaticAreas } from "../../shared/Util";
import { Address } from "../../shared/apis/AddressAPI";

interface Props {
  editing: boolean;
  setEditing: (value: boolean) => void;
  presentAddress: Address;
  permanentAddress: Address;
  onSubmit: (present: Address, permanent: Address) => void;
}

export default function AddressFormParent({ editing, setEditing, presentAddress, permanentAddress, onSubmit }: Props) {
  const [present, setPresent] = useState<Address>(presentAddress);
  const [permanent, setPermanent] = useState<Address>(permanentAddress);
  const [sameAsPresent, setSameAsPresent] = useState<boolean>(JSON.stringify(presentAddress) === JSON.stringify(permanentAddress));


  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const finalPermanent = sameAsPresent ? present : permanent;
    onSubmit(present, finalPermanent);
  }

  const renderReadOnly = (title: string, address: Address) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Grid container spacing={2} sx={{ pl: 2 }}>
        <Grid>
          <Typography variant="body2">
            <strong>House No:</strong> {address?.houseNo || "-"}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="body2">
            <strong>Street / Road:</strong> {address?.streetRoad || "-"}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="body2">
            <strong>Area:</strong> {address?.areaLocality || "-"}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="body2">
            <strong>Landmark:</strong> {address?.landmark || "-"}
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ pl: 2, mt: 0.5 }}>
        <Grid>
          <Typography variant="body2">
            <strong>City:</strong> {address?.city || "-"}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="body2">
            <strong>State:</strong> {address?.state || "-"}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="body2">
            <strong>Pincode:</strong> {address?.pincode || "-"}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );

  const renderForm = (title: string, address: Address, setAddress: (addr: Address) => void) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <Grid container spacing={2} marginTop={1}>
        <Grid>
          <TextField
            label="House No"
            size="small"
            required
            value={address?.houseNo || ""}
            onChange={(e) => setAddress({ ...address, houseNo: e.target.value })}
            sx={{ width: "120px" }}
          />
        </Grid>
        <Grid>
          <TextField
            label="Street / Road"
            size="small"
            value={address?.streetRoad || ""}
            onChange={(e) => setAddress({ ...address, streetRoad: e.target.value })}
            sx={{ width: "250px" }}
          />
        </Grid>
        <Grid>
          <SharedMUIList
            lable="Area / Locality"
            value={address?.areaLocality || " "}
            onChange={(val) => setAddress({ ...address, areaLocality: val })}
            list={getStaticAreas()}
            width="250px"
            required
          />
        </Grid>
        <Grid>
          <TextField
            label="Landmark"
            size="small"
            value={address?.landmark || ""}
            onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
            sx={{ width: "200px" }}
          />
        </Grid>
        <Grid>
          <TextField
            label="City"
            size="small"
            required
            value={address?.city || ""}
            onChange={(e) => setAddress({ ...address, city: e.target.value })}
            sx={{ width: "150px" }}
          />
        </Grid>
        <Grid>
          <SharedMUIList
            lable="State"
            value={address?.state || " "}
            onChange={(val) => setAddress({ ...address, state: val })}
            list={getStates()}
            width="200px"
            required
          />
        </Grid>
        <Grid>
          <NumberField
            label="Pincode"
            minDigits={6}
            maxDigits={6}
            onChange={(val) => setAddress({ ...address, pincode: val })}
            value={address?.pincode || undefined}
            required
            width="100px"
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2 }}>
      {/* Header with buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Address Information</Typography>
        {!editing ? (
          <Button onClick={() => setEditing(true)}>Edit</Button>
        ) : (
          <Box>
            <Button type="submit" variant="contained" size="small" sx={{ mr: 1 }}>
              Save
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPresent(presentAddress);
                setPermanent(permanentAddress);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      {/* Address sections */}
      {!editing ? (
        <>
          {renderReadOnly("Present Address", present)}
          <Typography variant="subtitle2">Permanent address is {sameAsPresent ? "same as Present" : ""}</Typography>
          {!sameAsPresent && renderReadOnly("Permanent Address", permanent)}
        </>
      ) : (
        <>
          {renderForm("Present Address", present, setPresent)}
          <FormControlLabel
            control={<Checkbox checked={sameAsPresent} onChange={(e) => setSameAsPresent(e.target.checked)} />}
            label="Permanent address is same as Present"
          />
          {!sameAsPresent && renderForm("Permanent Address", permanent, setPermanent)}
        </>
      )}
    </Box>
  );
}
